import { useEffect, useState } from 'react';
import type { AdminPackageDetail } from '../../types/packages';
import { buildPolicyRows, ImageSize, PolicyResult } from './packagePolicy';

export interface PackagePolicyChecklistProps {
  detail: AdminPackageDetail;
}

/** Natural size of the cover, for the 2:1 ≥ 800 px row. Null while loading or when it fails. */
function useImageSize(url: string | null | undefined): ImageSize | null {
  const [size, setSize] = useState<ImageSize | null>(null);
  useEffect(() => {
    setSize(null);
    if (!url) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => { if (!cancelled) setSize({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.src = url;
    return () => { cancelled = true; };
  }, [url]);
  return size;
}

const RESULT_TEXT: Record<PolicyResult, string> = { pass: 'Pass', fail: 'Fail', check: 'Check' };
/** Reuses the offer checklist colours: ok = green, warn = red, check = blue. */
const RESULT_LEVEL: Record<PolicyResult, string> = { pass: 'ok', fail: 'warn', check: 'check' };

/** Spec 081b — pass/fail rows computed from the detail. A rubric only: not sent, does not gate the decision. */
export default function PackagePolicyChecklist(props: PackagePolicyChecklistProps) {
  const { detail } = props;
  const size = useImageSize(detail.cover?.downloadUrl);
  const rows = buildPolicyRows(detail, size);

  return (
    <div className="admin-card offer-policy" data-testid="package-policy">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Policy checklist</h3>
      </div>
      <ul className="offer-policy__list">
        {rows.map((row) => (
          <li
            key={row.id}
            className={`offer-policy__item offer-policy__item--${RESULT_LEVEL[row.result]}`}
            data-testid={`package-policy-${row.id}`}
            data-result={row.result}
          >
            <div className="offer-policy__label">
              <span className="offer-policy__rule">{row.rule}</span>
            </div>
            <div className="offer-policy__hint">
              <span className={`offer-policy__level offer-policy__level--${RESULT_LEVEL[row.result]}`}>{RESULT_TEXT[row.result]}</span>
              {row.hint}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
